/**
 * The script for PresenceScene: four teammates in one bullet-journal page. Every step is a
 * pure change to the page at a fixed time, so any moment of the loop can be rebuilt by
 * replaying the steps before it (the server render, reduced motion and seeking all do).
 */
export type Pen = "ana" | "ben" | "chloe" | "dev";

export type Bullet = "task" | "done" | "event" | "note";

export interface Line {
  id: string;
  bullet: Bullet;
  text: string;
}

export interface Range {
  line: string;
  start: number;
  end: number;
}

export interface Cursor {
  line: string;
  at: number;
  /** Where a selection started; the selection runs from here to `at`. */
  anchor?: number;
}

export interface Page {
  lines: Line[];
  cursors: Partial<Record<Pen, Cursor>>;
  highlight?: Range;
  circle?: Range;
  /** Text typed while its writer was offline, waiting to merge. */
  pending?: Range;
  /** Text that has just merged, flashed once. */
  merged?: Range;
  done: string[];
  notes: Partial<Record<Pen, string>>;
  offline: Pen[];
  /** Dev's pencil, drawing a ring around `circle`. */
  pencil: boolean;
  stamp: boolean;
  status: string;
  fading: boolean;
}

export const PEOPLE: Record<Pen, { name: string; color: string }> = {
  ana: { name: "Ana", color: "#23706f" },
  ben: { name: "Ben", color: "#94640c" },
  chloe: { name: "Chloé", color: "#6d4a8c" },
  dev: { name: "Dev", color: "#b14a24" },
};

export const PENS = Object.keys(PEOPLE) as Pen[];

const DATE = "Pick a date: Nov 14th";
const LUNCH = "Lunch at the dumpling place?";

export const INITIAL: Page = {
  lines: [
    { id: "date", bullet: "task", text: DATE },
    { id: "moodboard", bullet: "task", text: "Send the moodboard to Kai" },
    { id: "venue", bullet: "event", text: "Venue call, Friday" },
    { id: "lunch", bullet: "note", text: LUNCH },
  ],
  cursors: { ana: { line: "lunch", at: LUNCH.length }, ben: { line: "moodboard", at: 0 } },
  done: [],
  notes: {},
  offline: [],
  pencil: false,
  stamp: false,
  status: "4 people in this page",
  fading: false,
};

type Step = { at: number; apply: (page: Page) => Page };

const steps: Step[] = [];
const at = (time: number, apply: (page: Page) => Page) => steps.push({ at: time, apply });
const move = (pen: Pen, cursor: Cursor | undefined) => (page: Page) => ({
  ...page,
  cursors: { ...page.cursors, [pen]: cursor },
});

/** A deterministic wobble, so typing feels human and renders the same on server and client. */
const jitter = (seed: number) => Math.abs((Math.sin(seed * 12.9898) * 43758.5453) % 1) * 0.6 + 0.7;

/** Types `text` at `pen`'s cursor, one character per step. Returns when it finishes. */
function type(pen: Pen, start: number, text: string, pace: number) {
  let time = start;

  for (let index = 0; index < text.length; index++) {
    const char = text[index];

    at(time, (page) => {
      const cursor = page.cursors[pen]!;

      return {
        ...page,
        lines: page.lines.map((line) =>
          line.id === cursor.line
            ? { ...line, text: line.text.slice(0, cursor.at) + char + line.text.slice(cursor.at) }
            : line,
        ),
        cursors: { ...page.cursors, [pen]: { ...cursor, at: cursor.at + 1 } },
        pending:
          page.pending && page.offline.includes(pen)
            ? { ...page.pending, end: page.pending.end + 1 }
            : page.pending,
      };
    });
    time += Math.round(pace * jitter(start + index) + (char === " " ? pace * 0.6 : 0));
  }

  return time;
}

// Chloé arrives and selects the date.
const dateStart = DATE.indexOf("Nov");

at(500, move("chloe", { line: "date", at: dateStart }));
for (let index = 1; index <= DATE.length - dateStart; index++)
  at(1100 + index * 70, move("chloe", { line: "date", at: dateStart + index, anchor: dateStart }));

// Ana starts a new task while Dev adds the time to the venue call: two people typing at once.
at(900, (page) => ({
  ...page,
  lines: [...page.lines, { id: "train", bullet: "task", text: "" }],
  cursors: { ...page.cursors, ana: { line: "train", at: 0 } },
}));
type("ana", 1050, "Book train tickets for everyone", 58);
at(1500, move("dev", { line: "venue", at: "Venue call, Friday".length }));
type("dev", 1900, " at 3pm", 82);

// Ben ticks off the moodboard.
at(2100, (page) => ({
  ...page,
  lines: page.lines.map((line) => (line.id === "moodboard" ? { ...line, bullet: "done" } : line)),
  done: [...page.done, "moodboard"],
}));

// Chloé highlights the date and leaves a note in the margin.
at(2500, (page) => ({
  ...page,
  highlight: { line: "date", start: dateStart, end: DATE.length },
  cursors: { ...page.cursors, chloe: { line: "date", at: DATE.length } },
}));
at(2900, (page) => ({ ...page, notes: { ...page.notes, chloe: "works for me!" } }));

// Dev rings the dumpling place with a pencil and scribbles beside it.
at(3400, (page) => ({
  ...page,
  cursors: { ...page.cursors, dev: undefined },
  pencil: true,
  circle: { line: "lunch", start: LUNCH.indexOf("dumpling"), end: LUNCH.length },
}));
at(4500, (page) => ({ ...page, pencil: false, notes: { ...page.notes, dev: "yum ♡" } }));

// Ben loses signal on the train, keeps writing, and merges when he's back.
at(5200, (page) => ({
  ...page,
  offline: ["ben"],
  status: "Ben is offline · still writing",
  cursors: { ...page.cursors, ben: { line: "lunch", at: LUNCH.length } },
  pending: { line: "lunch", start: LUNCH.length, end: LUNCH.length },
}));

const back = type("ben", 5700, " (bring snacks)", 66) + 700;

at(back, (page) => ({
  ...page,
  offline: [],
  merged: page.pending,
  pending: undefined,
  status: "Ben is back · his edits merged",
}));
at(back + 600, (page) => ({ ...page, stamp: true }));

const fade = back + 3600;

at(fade, (page) => ({ ...page, fading: true }));

/** The loop length; the page starts over after this. */
export const DURATION = fade + 600;

export const STEPS = steps.sort((a, b) => a.at - b.at);

/** How many steps have happened by `time`. */
export function stepIndex(time: number) {
  let index = 0;

  while (index < STEPS.length && STEPS[index].at <= time) index++;

  return index;
}

/** The page after the first `count` steps. */
export function pageAt(count: number) {
  let page = INITIAL;

  for (let index = 0; index < count; index++) page = STEPS[index].apply(page);

  return page;
}

/** The finished page without the closing fade, for reduced motion. */
export const FINAL_INDEX = stepIndex(fade - 1);
